import React, { useState } from 'react';
import { MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { useLocale } from '../components/LocaleProvider';
import { SiteLayout } from '../components/SiteLayout';
type SubmissionStatus = 'idle' | 'sending' | 'success' | 'error';
type Web3FormsResponse = {
  success?: boolean;
  message?: string;
};
export function ContactPage() {
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [submissionError, setSubmissionError] = useState('');
  const {
    locale
  } = useLocale();
  const text = locale === 'fr' ? {
    title: 'Trouvons ce qu’il vous faut.',
    body: 'Interrogez-nous sur les produits, la disponibilité en rayon, vos besoins professionnels ou une demande de devis.',
    phone: 'Coordonnées disponibles par téléphone',
    name: 'Nom complet',
    email: 'Adresse e-mail',
    need: 'Votre demande',
    placeholder: 'Produits, quantités ou renseignements sur un rayon',
    send: 'Envoyer la demande',
    sending: 'Envoi en cours…',
    success: 'Merci. Notre équipe vous répondra sous un jour ouvré.',
    error: 'Votre demande n’a pas pu être envoyée. Veuillez réessayer ou nous appeler.'
  } : {
    title: 'Let’s find what you need.',
    body: 'Ask about products, department availability, business requirements or a quotation.',
    phone: 'Contact details available by phone',
    name: 'Full name',
    email: 'Email address',
    need: 'What do you need?',
    placeholder: 'Products, quantities or department information',
    send: 'Send request',
    sending: 'Sending…',
    success: 'Thank you. Our team will respond within one business day.',
    error: 'Your request could not be sent. Please try again or call us.'
  };
  async function submitContactForm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('sending');
    setSubmissionError('');
    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = {
      ...Object.fromEntries(formData.entries()),
      access_key: '9f6d2e40-eb5c-4438-9a46-6e175eae72f4',
      subject: locale === 'fr' ? 'Nouvelle demande de contact — Société Supermarches Charentais' : 'New contact enquiry — Société Supermarches Charentais',
      from_name: 'Société Supermarches Charentais',
      language: locale === 'fr' ? 'Français' : 'English'
    };
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result: Web3FormsResponse = await response.json();
      if (!response.ok || !result.success) {
        setSubmissionError(result.message ?? '');
        setStatus('error');
        return;
      }
      form.reset();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }
  return <SiteLayout data-id="element-115">
      <main className="mx-auto grid max-w-[1100px] gap-12 px-6 py-20 md:grid-cols-[0.8fr_1.2fr]" data-id="element-116">
        <div data-id="element-117">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#087c83]" data-id="element-118">
            Société Supermarches Charentais
          </p>
          <h1 className="mt-4 text-5xl font-extrabold tracking-[-0.05em] text-[#102b43]" data-id="element-119">
            {text.title}
          </h1>
          <p className="mt-5 leading-7 text-slate-600" data-id="element-120">{text.body}</p>
          <div className="mt-8 space-y-5 text-sm text-slate-600" data-id="element-121">
            <p className="flex gap-3" data-id="element-122">
              <PhoneIcon className="shrink-0 text-[#087c83]" data-id="element-123" />
              +33 9 77 21 80 15
            </p>
            <p className="flex gap-3" data-id="element-124">
              <MailIcon className="shrink-0 text-[#087c83]" data-id="element-125" />
              {text.phone}
            </p>
            <p className="flex gap-3" data-id="element-126">
              <MapPinIcon className="shrink-0 text-[#087c83]" data-id="element-127" />
              Société Supermarches Charentais (Centre Leclerc), Avenue du Fief
              Rose, 17140 Lagord, France
            </p>
          </div>
          <div className="mt-8 border-t border-slate-200 pt-5 text-xs leading-6 text-slate-500" data-id="element-128">
            <p data-id="element-129">SIREN: 781 622 378 · SIRET: 781 622 378 00037</p>
            <p data-id="element-130">TVA: FR10 781622378</p>
          </div>
        </div>
        <form onSubmit={submitContactForm} className="rounded-2xl bg-white p-7 shadow-lg" data-id="element-131">
          <input type="checkbox" name="botcheck" className="hidden" tabIndex={-1} autoComplete="off" data-id="element-132" />
          <label className="block font-bold" data-id="element-133">
            {text.name}
            <input required name="name" autoComplete="name" className="mt-2 w-full rounded-lg border border-slate-200 p-3 font-normal" data-id="element-134" />
          </label>
          <label className="mt-5 block font-bold" data-id="element-135">
            {text.email}
            <input required name="email" type="email" autoComplete="email" className="mt-2 w-full rounded-lg border border-slate-200 p-3 font-normal" data-id="element-136" />
          </label>
          <label className="mt-5 block font-bold" data-id="element-137">
            {text.need}
            <textarea required name="message" rows={5} className="mt-2 w-full rounded-lg border border-slate-200 p-3 font-normal" placeholder={text.placeholder} data-id="element-138" />
          </label>
          <button type="submit" disabled={status === 'sending'} className="mt-6 rounded-lg bg-[#087c83] px-5 py-4 text-xs font-bold uppercase tracking-wider text-white disabled:cursor-not-allowed disabled:opacity-70" data-id="element-139">
            {status === 'sending' ? text.sending : text.send}
          </button>
          {status === 'success' && <p role="status" className="mt-4 rounded-lg bg-[#e8f2f3] p-3 text-sm font-bold text-[#087c83]" data-id="element-140">
              {text.success}
            </p>}
          {status === 'error' && <div role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm font-bold text-red-700" data-id="element-141">
              <p data-id="element-142">{text.error}</p>
              {submissionError && <p className="mt-1 text-xs font-medium" data-id="element-143">{submissionError}</p>}
            </div>}
        </form>
      </main>
    </SiteLayout>;
}